/**
 * usePaymentActions Hooks
 *
 * URQL mutation hooks for approving/rejecting payments.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const ApprovePaymentDocument = `
  mutation ApprovePayment($orderId: ID!) {
    approvePayment(orderId: $orderId) {
      success
      message
    }
  }
`;

const RejectPaymentDocument = `
  mutation RejectPayment($orderId: ID!, $reason: String!) {
    rejectPayment(orderId: $orderId, reason: $reason) {
      success
      message
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface PaymentActionResult {
  success: boolean;
  message: string | null;
}

interface UseApprovePaymentResult {
  approvePayment: (orderId: string) => Promise<PaymentActionResult>;
  isLoading: boolean;
  error: Error | null;
}

interface UseRejectPaymentResult {
  rejectPayment: (orderId: string, reason: string) => Promise<PaymentActionResult>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook for approving a payment.
 */
export function useApprovePayment(): UseApprovePaymentResult {
  const [result, executeMutation] = useMutation<
    { approvePayment: PaymentActionResult },
    { orderId: string }
  >(ApprovePaymentDocument);

  const approvePayment = useCallback(
    async (orderId: string): Promise<PaymentActionResult> => {
      try {
        const response = await executeMutation({ orderId });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось подтвердить оплату");
          const err = new Error(message);
          throw err;
        }

        if (!response.data?.approvePayment) {
          throw new Error("Сервер не вернул данные");
        }

        const result = response.data.approvePayment;

        if (!result.success) {
          throw new Error(result.message || "Не удалось подтвердить оплату");
        }

        errorTracking.addBreadcrumb({
          category: "admin.payments",
          message: "Payment approved",
          data: { orderId },
        });

        return result;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Не удалось подтвердить оплату");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "approvePayment" },
          extra: { orderId },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    approvePayment,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

/**
 * Hook for rejecting a payment.
 */
export function useRejectPayment(): UseRejectPaymentResult {
  const [result, executeMutation] = useMutation<
    { rejectPayment: PaymentActionResult },
    { orderId: string; reason: string }
  >(RejectPaymentDocument);

  const rejectPayment = useCallback(
    async (orderId: string, reason: string): Promise<PaymentActionResult> => {
      try {
        const response = await executeMutation({ orderId, reason });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось отклонить оплату");
          const err = new Error(message);
          throw err;
        }

        if (!response.data?.rejectPayment) {
          throw new Error("Сервер не вернул данные");
        }

        const result = response.data.rejectPayment;

        if (!result.success) {
          throw new Error(result.message || "Не удалось отклонить оплату");
        }

        errorTracking.addBreadcrumb({
          category: "admin.payments",
          message: "Payment rejected",
          data: { orderId, reason },
        });

        return result;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Не удалось отклонить оплату");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "rejectPayment" },
          extra: { orderId, reason },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    rejectPayment,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
